import { useMutation } from '@tanstack/react-query'
import { httpClient } from '../../shared/api/httpClient'
import { useAstreaEnvironment } from '../../shared/hooks/useAstreaEnvironment'

/**
 * Port of `app/histories/historiesService.js`'s `loadHistories` +
 * `app/histories/historiesController.js`'s `load(cursor)` recursion to a
 * TanStack Query mutation hook.
 *
 * Legacy behavior (confirmed by reading the AngularJS source):
 *
 *   HistoriesService.loadHistories = function({ serialNumber, jobId, cursor }) {
 *     return $http.get(`${AuthService.getUrlAstreaPath()}/api/v2/historical/serial/${serialNumber}`, {
 *       params: { cursor, jobId, limit: 100 },
 *       headers: { Authorization: AuthService.getServletBasicAuth().replace('Basic ', '') }
 *     });
 *   };
 *
 *   function load(cursor) {
 *     $scope.data.loading = true;
 *     HistoriesService.loadHistories({...$scope.form, cursor})
 *       .then(({ data: { cursor: responseCursor, historicals = [] }}) => {
 *         $scope.data.list = [...$scope.data.list, ...historicals.map(h => ({...h, formattedDate: ...}))];
 *         if (responseCursor) return load(responseCursor);
 *         $scope.data.loading = false;
 *       })
 *       .catch(error => { ... Materialize.toast(...) });
 *   }
 *
 * `AuthService.getUrlAstreaPath()` resolves to the *external* Astrea
 * backend base URL for the user's selected environment (LOCAL/HOMOLOG/
 * PROD/PLAYGROUND) — not this repo's own `server_app` (confirmed via
 * `grep -rn "historical" server_app/` — no hits: `server_app` has no route
 * for this at all, the call always went straight from the browser to Astrea).
 * Mirrors the same pattern already established by
 * `client/src/modules/logoutUsers/useLogoutUsersMutation.js` (task running
 * concurrently on a sibling module): `getUrlAstreaPath(getUrlAstrea())` for
 * the base URL, and an absolute URL passed to `httpClient` so axios ignores
 * its configured `baseURL` (https://axios-http.com/docs/req_config).
 *
 * `Authorization: AuthService.getServletBasicAuth().replace('Basic ', '')`
 * is the same fixed servlet Basic Auth header that `httpClient`'s request
 * interceptor (task 008) already attaches unconditionally to every request
 * regardless of target host — so it does not need to be repeated here (see
 * `client/src/shared/api/httpClient.js`'s doc-comment for the full history
 * of that header/value).
 *
 * Pagination: the legacy `load(cursor)` recurses on every response that
 * carries a `cursor`, accumulating `historicals` into `$scope.data.list`
 * until the backend stops returning one. Ported 1:1 below as an async loop
 * inside the mutation's `mutationFn`, accumulating into a single array
 * returned once fully drained — `onSuccess` then replaces the page's list
 * state in one shot, matching the end state of the legacy recursion (the
 * intermediate partial-list renders during the loop have no equivalent
 * here since `useMutation` only exposes pending/success/error, not partial
 * progress; the DoD's "loading" indicator is covered by `isPending` alone,
 * same visible affordance as the legacy `data.loading` flag).
 *
 * `formattedDate`: legacy formats with `moment(historical.date).format('DD/MM/YYYY HH:mm')`.
 * `moment` is not a dependency of the React client (`client/package.json`
 * has no `moment`, unlike the legacy `package.json`) — rather than adding a
 * new date library dependency purely to replicate one `.format()` call,
 * this uses a small local formatter built on the native `Intl.DateTimeFormat`
 * (`formatHistoryDate` below), which reproduces the same `DD/MM/YYYY HH:mm`
 * output (pt-BR, 24h, zero-padded) for any parseable `historical.date`
 * value. This is a deliberate dependency-avoidance decision, not a
 * behavioral change — flagged here per this task's instructions instead of
 * silently adding/skipping a library.
 */

const HISTORICAL_PAGE_LIMIT = 100

/** `DD/MM/YYYY HH:mm`, matching the legacy `moment(...).format('DD/MM/YYYY HH:mm')` output. */
export function formatHistoryDate(date) {
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return ''

  const pad = (value) => String(value).padStart(2, '0')
  return (
    `${pad(parsed.getDate())}/${pad(parsed.getMonth() + 1)}/${parsed.getFullYear()} ` +
    `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
  )
}

async function fetchHistoricalPage({ baseUrl, serialNumber, jobId, cursor }) {
  const response = await httpClient.get(`${baseUrl}/api/v2/historical/serial/${serialNumber}`, {
    params: { cursor, jobId, limit: HISTORICAL_PAGE_LIMIT },
  })
  return response.data
}

export function useHistoriesQuery() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async ({ serialNumber, jobId }) => {
      let cursor = ''
      let historicals = []
      // Ported 1:1 from `load(cursor)`'s recursion: keep requesting the next
      // page while the backend returns a `cursor`, stopping as soon as it
      // comes back falsy.
      do {
        const page = await fetchHistoricalPage({ baseUrl, serialNumber, jobId, cursor })
        historicals = [...historicals, ...(page.historicals || [])]
        cursor = page.cursor
      } while (cursor)

      return historicals.map((historical) => ({
        ...historical,
        formattedDate: formatHistoryDate(historical.date),
      }))
    },
  })
}

export default useHistoriesQuery
